import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Box, BoxProps, Text } from 'rebass';
import { Input, Label } from '../Form';
import { WaterHazardItem } from '@/modes/admin/pages/waterHazards/WaterHazards.query';
import ButtonToggle from '../Button/ButtonToggle';
import UploadDrop from '../File/UploadDrop';
import Form from '../Form/Form';
import FormRow from '../Form/FormRow';
import FormColumn from '../Form/FormColumn';
import { GrowyTextarea } from '@/components/Form/GrowyTextarea';
import { GridTable } from '../Table/GridTable';
import assembliesColumns from './SurveyTemplateAssemblies.columns';
import { SurveyTemplateInput } from './SurveyTemplateForm';
import Button from '../Button/Button';
import Modal, { ModalBody, ModalFooter, ModalHeader } from '../Modal';
import { HazardAnswer } from './types';
import {
  addHazardSurveyAttachment,
  getHazardSurveyUploadUrl,
  HazardSurveyAttachment,
  HazardSurveySubmitPayload,
} from '@/services/hazardSurvey';
import FilePreviews from '../File/FilePreviews';
import InlineNotice from '../InlineNotice/InlineNotice';
import {
  HazardInputRow,
  HazardResultRow,
  makeHazardInputColumns,
  makeHazardResultColumns,
} from './hazardSurveyResultColumns';

export type HazardSurveyResultMode = 'preview' | 'fill' | 'results';

export type KnownHazardWithProtection = {
  id: string;
  name: string;
  hazardLevel: 'LOW' | 'HIGH';
  isProtected?: 'YES' | 'NO' | 'UNKNOWN' | null;
  backflowAssemblyId?: string | null;
};

export type HazardSurveyResultProps = BoxProps & {
  template: SurveyTemplateInput;
  hazards: WaterHazardItem[];
  knownHazards?: KnownHazardWithProtection[];
  hideName?: boolean;
  mode?: HazardSurveyResultMode;
  waterService?: {
    siteNumber: string | null;
    meterNumber: string | null;
    meterSize?: number | null;
    address: {
      street1: string;
      street2?: string | null;
    };
    backflowAssemblies: any[];
  };
  /** Initial form values (for 'results' mode or pre-filling) */
  initialValues?: HazardSurveySubmitPayload;
  /** Called when the user confirms submission. Only used in 'fill' mode. */
  onSubmit?: (values: HazardSurveySubmitPayload) => Promise<void> | void;
  /** Public survey token — required in 'fill' mode to upload photo attachments. */
  surveyToken?: string;
  /** Already-uploaded attachments to display. */
  attachments?: HazardSurveyAttachment[];
  /** Called after a new attachment is successfully uploaded. */
  onAttachmentUploaded?: () => void;
  /** Alternative upload handler for authenticated (internal) use — bypasses surveyToken REST path. */
  onUploadPhoto?: (file: File) => Promise<void>;
  /** Water supplier branding to display in the header. */
  branding?: { name: string | null; logoUrl: string | null } | null;
  /** Suppress the drop shadow (e.g. when rendered inside a dialog). */
  noShadow?: boolean;
  /** Hide the branding/logo header and water service info header. */
  hideHeader?: boolean;
  /** Called when the user clicks Submit (validation passes) — lets the parent render the button elsewhere. */
  onRequestSubmit?: (values: HazardSurveySubmitPayload) => void;
  /** Ref that will be set to the internal validate-and-submit handler, so a parent footer button can trigger it. */
  submitTriggerRef?: React.MutableRefObject<
    ((e: React.MouseEvent) => void) | null
  >;
};

export const HazardSurveyResult: React.FC<HazardSurveyResultProps> = ({
  template,
  hazards,
  hideName,
  mode: modeProp,
  waterService,
  initialValues,
  onSubmit,
  surveyToken,
  attachments,
  onAttachmentUploaded,
  onUploadPhoto,
  branding,
  noShadow,
  hideHeader,
  onRequestSubmit,
  submitTriggerRef,
  knownHazards,
  ...props
}) => {
  // Resolve effective mode: legacy readOnly prop maps to 'results'
  const mode: HazardSurveyResultMode = modeProp ?? 'preview';
  const isInteractive = mode === 'fill';
  const isReadOnly = mode === 'results';

  const selectedHazards = hazards.filter((h) =>
    template.hazardIds.includes(h.id)
  );

  // Form state
  const [submitterName, setSubmitterName] = useState(
    initialValues?.submitterName ?? ''
  );
  const [submitterPhone, setSubmitterPhone] = useState(
    initialValues?.submitterPhone ?? ''
  );
  const [submitterEmail, setSubmitterEmail] = useState(
    initialValues?.submitterEmail ?? ''
  );
  const [hazardAnswers, setHazardAnswers] = useState<
    Record<string, HazardAnswer>
  >(() => {
    if (initialValues?.hazards) return initialValues.hazards;
    // In preview mode, default all hazards to 'YES' so protection column is visible
    if (mode === 'preview') {
      return Object.fromEntries(
        selectedHazards.map((h) => [h.id, 'YES' as HazardAnswer])
      );
    }
    // In fill mode, pre-seed known hazards as 'YES' (they are present on the water service)
    if (mode === 'fill' && knownHazards?.length) {
      return Object.fromEntries(
        knownHazards.map((h) => [h.id, 'YES' as HazardAnswer])
      );
    }
    return {};
  });
  const [protectedByHazard, setProtectedByHazard] = useState<
    Record<string, HazardAnswer | null>
  >(() => {
    if (initialValues?.protectedByHazard)
      return initialValues.protectedByHazard;
    // In preview mode, seed protection answers so the column shows meaningful content
    if (mode === 'preview') {
      return Object.fromEntries(
        selectedHazards.map((h, i) => [
          h.id,
          (i % 2 === 0 ? 'YES' : 'NO') as HazardAnswer,
        ])
      );
    }
    // In fill mode, pre-seed known hazards' protection status from water service data
    if (mode === 'fill' && knownHazards?.length) {
      return Object.fromEntries(
        knownHazards
          .filter((h) => h.isProtected)
          .map((h) => [h.id, h.isProtected as HazardAnswer])
      );
    }
    return {};
  });
  // In fill mode, seed known hazard answers/protection when knownHazards prop arrives after mount
  React.useEffect(() => {
    if (mode !== 'fill' || !knownHazards?.length) return;
    setHazardAnswers((prev) => {
      const next = { ...prev };
      knownHazards.forEach((h) => {
        if (!next[h.id]) next[h.id] = 'YES';
      });
      return next;
    });
    setProtectedByHazard((prev) => {
      const next = { ...prev };
      knownHazards.forEach((h) => {
        if (h.isProtected && !next[h.id])
          next[h.id] = h.isProtected as HazardAnswer;
      });
      return next;
    });
    setBackflowAssemblyByHazard((prev) => {
      const next = { ...prev };
      knownHazards.forEach((h) => {
        if (h.backflowAssemblyId && !next[h.id])
          next[h.id] = h.backflowAssemblyId;
      });
      return next;
    });
  }, [mode, knownHazards]);

  // In preview mode, keep hazard answers seeded to 'YES' whenever selectedHazards changes
  // (useState lazy init only fires once on mount; this effect handles subsequent prop changes)
  React.useEffect(() => {
    if (mode !== 'preview') return;
    setHazardAnswers((prev) => {
      const next = { ...prev };
      let changed = false;
      selectedHazards.forEach((h) => {
        if (!next[h.id]) {
          next[h.id] = 'YES';
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    setProtectedByHazard((prev) => {
      const next = { ...prev };
      let changed = false;
      selectedHazards.forEach((h, i) => {
        if (!next[h.id]) {
          next[h.id] = (i % 2 === 0 ? 'YES' : 'NO') as HazardAnswer;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [mode, template.hazardIds]);

  const [backflowAssemblyByHazard, setBackflowAssemblyByHazard] = useState<
    Record<string, string | null>
  >(() => {
    if (initialValues?.backflowAssemblyByHazard)
      return initialValues.backflowAssemblyByHazard;
    // In fill mode, pre-seed known hazards' assembly from water service data
    if (mode === 'fill' && knownHazards?.length) {
      return Object.fromEntries(
        knownHazards
          .filter((h) => h.backflowAssemblyId)
          .map((h) => [h.id, h.backflowAssemblyId as string])
      );
    }
    return {};
  });
  const [otherHazards, setOtherHazards] = useState<HazardAnswer | undefined>(
    initialValues?.otherHazards
  );
  const [comments, setComments] = useState(initialValues?.comments ?? '');
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const onDropPhoto = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;
      setUploadingPhoto(true);
      try {
        if (onUploadPhoto) {
          for (const file of acceptedFiles) {
            await onUploadPhoto(file);
            onAttachmentUploaded?.();
          }
        } else if (surveyToken) {
          for (const file of acceptedFiles) {
            const { url, key } = await getHazardSurveyUploadUrl(
              surveyToken,
              file.type
            );
            const s3Res = await fetch(url, {
              method: 'PUT',
              headers: { 'Content-Type': file.type },
              body: file,
            });
            if (!s3Res.ok) {
              throw new Error(
                `Failed to upload file to storage (${s3Res.status})`
              );
            }
            await addHazardSurveyAttachment(surveyToken, key, file.type);
            onAttachmentUploaded?.();
          }
        }
      } finally {
        setUploadingPhoto(false);
      }
    },
    [surveyToken, onUploadPhoto, onAttachmentUploaded]
  );

  const canUpload = !!onUploadPhoto || !!surveyToken;

  const photoDropzone = useDropzone({
    onDrop: onDropPhoto,
    accept: { 'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.webp'] },
    multiple: true,
    disabled: !canUpload || uploadingPhoto,
  });

  const mockWaterService = {
    siteNumber: '12345',
    meterNumber: 'M-123456',
    meterSize: 1.5,
    address: {
      street1: '123 Main St',
      street2: 'Suite 100',
    },
    backflowAssemblies: [
      {
        id: '1',
        serialNumber: 'SN-12345',
        location: 'North side of building',
        type: 'RP',
        lastTestDate: '2023-01-15',
        model: {
          name: '825Y',
          backflowAssemblyMake: {
            name: 'Febco',
          },
        },
        lastTest: {
          finalResult: 'PASS',
          initialResult: 'PASS',
        },
      },
      {
        id: '2',
        serialNumber: 'SN-67890',
        location: 'Mechanical room',
        type: 'DC',
        lastTestDate: '2023-06-20',
        model: {
          name: '975XL',
          backflowAssemblyMake: {
            name: 'Zurn',
          },
        },
        lastTest: {
          finalResult: 'FAIL',
          initialResult: 'FAIL',
        },
      },
    ],
  };

  const service = waterService || mockWaterService;

  const hazardOptions = [
    { label: 'Yes', value: 'YES' },
    { label: 'No', value: 'NO' },
    { label: 'Unsure', value: 'UNKNOWN' },
  ];

  const hasOtherHazards = otherHazards === 'YES';
  const currentValues = (): HazardSurveySubmitPayload => ({
    submitterName,
    submitterPhone,
    submitterEmail,
    hazards: hazardAnswers,
    protectedByHazard: template.protectedQuestion
      ? protectedByHazard
      : undefined,
    backflowAssemblyByHazard: template.protectedQuestion
      ? backflowAssemblyByHazard
      : undefined,
    otherHazards,
    comments: comments || undefined,
  });

  const validate = (): string[] => {
    const errors: string[] = [];
    if (!submitterName.trim()) errors.push('Full name is required.');
    if (!submitterPhone.trim()) errors.push('Phone number is required.');
    if (!submitterEmail.trim()) errors.push('Email address is required.');
    for (const hazard of selectedHazards) {
      if (!hazardAnswers[hazard.id])
        errors.push(`Please answer the question for "${hazard.name}".`);
    }
    if (template.otherHazardsQuestion && !otherHazards)
      errors.push(
        'Please answer "Are there any other known hazards not listed?"'
      );
    if (
      template.otherHazardsQuestion &&
      otherHazards === 'YES' &&
      !comments.trim()
    )
      errors.push('Comments are required when other hazards are present.');
    return errors;
  };

  const handleValidateAndSubmit = React.useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const errors = validate();
      if (errors.length > 0) {
        setValidationErrors(errors);
        return;
      }
      setValidationErrors([]);
      if (onRequestSubmit) {
        onRequestSubmit(currentValues());
      } else {
        setShowConfirm(true);
      }
    },
    [
      submitterName,
      submitterPhone,
      submitterEmail,
      hazardAnswers,
      otherHazards,
      comments,
      selectedHazards,
      template,
      onRequestSubmit,
    ]
  );

  React.useEffect(() => {
    if (submitTriggerRef) {
      submitTriggerRef.current = handleValidateAndSubmit;
    }
  }, [submitTriggerRef, handleValidateAndSubmit]);

  const handleConfirmSubmit = async () => {
    if (!onSubmit) return;
    setSubmitting(true);
    try {
      await onSubmit(currentValues());
    } finally {
      setSubmitting(false);
      setShowConfirm(false);
    }
  };

  return (
    <Box
      bg="white"
      mx="auto"
      {...(props as any)}
      sx={{
        ...(noShadow ? {} : { boxShadow: 'feature' }),
        borderRadius: 4,
        minHeight: '100%',
        width: '100%',
        maxWidth: 800,
        ...props.sx,
      }}
    >
      {showConfirm && (
        <Modal onClose={() => setShowConfirm(false)}>
          <ModalHeader title="Submit Survey" />
          <ModalBody>
            <Text>
              Are you ready to submit your answers? Once submitted, you will not
              be able to make changes.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="outline"
              onClick={() => setShowConfirm(false)}
              disabled={submitting}
            >
              Go Back
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirmSubmit}
              disabled={submitting}
            >
              {submitting ? 'Submitting…' : 'Submit'}
            </Button>
          </ModalFooter>
        </Modal>
      )}

      {!hideHeader && branding && (
        <Box mb={2} textAlign="center">
          {branding.logoUrl && (
            <Box mb={2}>
              <img
                src={branding.logoUrl}
                alt={branding.name || 'Water Supplier Logo'}
                style={{ maxHeight: 80, maxWidth: 240, objectFit: 'contain' }}
              />
            </Box>
          )}
          {branding.name && !branding.logoUrl && (
            <Text fontSize={2} color="gray.600" mb={1}>
              {branding.name}
            </Text>
          )}
        </Box>
      )}

      {!hideName && (
        <Text fontSize={4} textAlign="center">
          {template.name || 'Hazard Assessment Survey'}
        </Text>
      )}

      <Form
        sx={{
          button: {
            // px: 2,
            // py: 1,
          },
        }}
      >
        {!hideHeader && (
          <Box>
            <Label>Water Service Account</Label>
            <Box
              px={3}
              py={2}
              sx={{
                borderRadius: 'default',
                backgroundColor: 'paneBg',
              }}
            >
              <FormRow gridTemplateColumns="repeat(auto-fit, minmax(100px, auto))">
                <FormColumn>
                  <Label fontSize={0}>Address</Label>
                  <Text fontSize={1}>
                    {service.address?.street1}
                    {service.address?.street2
                      ? `, ${service.address.street2}`
                      : ''}
                  </Text>
                </FormColumn>
                <FormColumn>
                  <Label fontSize={0}>Account #</Label>
                  <Text fontSize={1}>{service.siteNumber}</Text>
                </FormColumn>
                <FormColumn>
                  <Label fontSize={0}>Meter</Label>
                  <Text fontSize={1}>
                    {service.meterNumber}{' '}
                    {service.meterSize ? `${service.meterSize}"` : ''}
                  </Text>
                </FormColumn>
              </FormRow>
            </Box>
          </Box>
        )}

        {service.backflowAssemblies?.length > 0 && (
          <FormRow>
            <FormColumn>
              <Label>Backflow Prevention Assemblies</Label>
              <GridTable
                columns={assembliesColumns}
                items={service.backflowAssemblies}
              />
            </FormColumn>
          </FormRow>
        )}

        {!isReadOnly && template.instructions && (
          <Text>{template.instructions}</Text>
        )}

        {!isReadOnly &&
          selectedHazards.length === 0 &&
          (!knownHazards || knownHazards.length === 0) &&
          !template.otherHazardsQuestion &&
          !template.protectedQuestion && (
            <InlineNotice>
              No hazards or extra questions selected yet. Add them to see them
              here.
            </InlineNotice>
          )}
        {(selectedHazards.length > 0 ||
          (knownHazards && knownHazards.length > 0)) &&
          (() => {
            const knownIds = new Set((knownHazards ?? []).map((h) => h.id));
            const knownAsHazards = (knownHazards ?? []).map((h) => ({
              id: h.id,
              name: h.name,
              description: '',
              hazardLevel: h.hazardLevel,
            }));
            const additionalHazards = selectedHazards.filter(
              (h) => !knownIds.has(h.id)
            );
            const allHazards = [...knownAsHazards, ...additionalHazards];
            return (
              <>
                {isReadOnly ? (
                  <GridTable
                    columns={makeHazardResultColumns(
                      service?.backflowAssemblies ?? []
                    )}
                    items={allHazards.map(
                      (hazard): HazardResultRow => ({
                        id: hazard.id,
                        name: hazard.name,
                        hazardLevel: hazard.hazardLevel as 'LOW' | 'HIGH',
                        answer: hazardAnswers[hazard.id] ?? null,
                        isProtected: protectedByHazard[hazard.id] ?? null,
                        backflowAssemblyId:
                          backflowAssemblyByHazard[hazard.id] ?? null,
                      })
                    )}
                  />
                ) : (
                  <GridTable
                    columns={makeHazardInputColumns({
                      assemblies: service?.backflowAssemblies ?? [],
                      showProtection: !!template.protectedQuestion,
                      isInteractive,
                      setHazardAnswer: (id, value) => {
                        setHazardAnswers((prev) => ({
                          ...prev,
                          [id]: value as HazardAnswer,
                        }));
                        if (value !== 'YES') {
                          setProtectedByHazard((prev) => {
                            const next = { ...prev };
                            delete next[id];
                            return next;
                          });
                          setBackflowAssemblyByHazard((prev) => {
                            const next = { ...prev };
                            delete next[id];
                            return next;
                          });
                        }
                      },
                      setProtectionAnswer: (id, value) => {
                        if (value === undefined) {
                          setProtectedByHazard((prev) => {
                            const next = { ...prev };
                            delete next[id];
                            return next;
                          });
                        } else {
                          setProtectedByHazard((prev) => ({
                            ...prev,
                            [id]: value,
                          }));
                        }
                        if (value !== 'YES') {
                          setBackflowAssemblyByHazard((prev) => {
                            const next = { ...prev };
                            delete next[id];
                            return next;
                          });
                        }
                      },
                      setAssemblyId: (id, assemblyId) => {
                        setBackflowAssemblyByHazard((prev) => ({
                          ...prev,
                          [id]: assemblyId,
                        }));
                      },
                    })}
                    items={allHazards.map(
                      (hazard): HazardInputRow => ({
                        id: hazard.id,
                        name: hazard.name,
                        description: hazard.description,
                        hazardLevel: hazard.hazardLevel as 'LOW' | 'HIGH',
                        answer: hazardAnswers[hazard.id],
                        protectionAnswer:
                          protectedByHazard[hazard.id] ?? undefined,
                        selectedAssemblyId: backflowAssemblyByHazard[hazard.id],
                      })
                    )}
                  />
                )}
              </>
            );
          })()}

        {template.otherHazardsQuestion && (
          <FormRow gridTemplateColumns="1fr auto">
            <FormColumn>
              <Text>Are there any other known hazards not listed?</Text>
            </FormColumn>
            <FormColumn sx={{ justifyContent: 'center' }}>
              <ButtonToggle
                required={isInteractive}
                disabled={!isInteractive}
                name="other-hazards"
                value={otherHazards}
                buttonOptions={hazardOptions.map((opt) => ({
                  ...opt,
                  onClick: isInteractive
                    ? () => setOtherHazards(opt.value as HazardAnswer)
                    : undefined,
                }))}
              />
            </FormColumn>
          </FormRow>
        )}

        {(!isReadOnly || !!comments) && (
          <FormRow>
            <FormColumn>
              <Label>Comments</Label>
              {isReadOnly ? (
                <Text fontSize={1}>{comments}</Text>
              ) : (
                <GrowyTextarea
                  disabled={!isInteractive}
                  placeholder="Enter comments"
                  variant="textarea"
                  required={isInteractive && hasOtherHazards}
                  maxCharacters={255}
                  value={comments}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    setComments(e.target.value)
                  }
                />
              )}
            </FormColumn>
          </FormRow>
        )}

        {mode === 'fill' && (
          <FormRow>
            <FormColumn>
              <Label>Photos</Label>
              <UploadDrop
                icon="image"
                multiple
                accept={{
                  'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
                }}
                dropZone={photoDropzone}
              />
              {uploadingPhoto && (
                <Text fontSize={1} color="gray.500" mt={1}>
                  Uploading…
                </Text>
              )}
              {attachments && attachments.length > 0 && (
                <FilePreviews files={attachments as any} mt={2} />
              )}
            </FormColumn>
          </FormRow>
        )}

        {validationErrors.length > 0 && (
          <FormRow>
            <FormColumn>
              <InlineNotice
                sx={{
                  borderColor: 'dangerHover',
                  backgroundColor: 'lightRed',
                }}
              >
                {validationErrors.map((err) => (
                  <Text key={err} fontSize={1} color="error">
                    {err}
                  </Text>
                ))}
              </InlineNotice>
            </FormColumn>
          </FormRow>
        )}

        {/* Submitter info — read-only in results mode, editable in fill/preview modes */}
        {isReadOnly && (submitterName || submitterPhone || submitterEmail) && (
          <Box>
            <strong>Submitted by:</strong>
            {submitterName && <span> {submitterName}</span>}
            {submitterEmail && <span> &bull; {submitterEmail}</span>}
            {submitterPhone && <span> &bull; {submitterPhone}</span>}
          </Box>
        )}

        {!isReadOnly && (
          <FormRow gridTemplateColumns="1fr 1fr 1fr">
            <FormColumn>
              <Label htmlFor="submitter-name">Full Name</Label>
              <Input
                id="submitter-name"
                width="100%"
                placeholder="Your full name"
                value={submitterName}
                required={isInteractive}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setSubmitterName(e.target.value)
                }
                disabled={!isInteractive}
              />
            </FormColumn>
            <FormColumn>
              <Label htmlFor="submitter-phone">Phone Number</Label>
              <Input
                width="100%"
                id="submitter-phone"
                placeholder="Your phone number"
                required={isInteractive}
                value={submitterPhone}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setSubmitterPhone(e.target.value)
                }
                disabled={!isInteractive}
              />
            </FormColumn>
            <FormColumn>
              <Label htmlFor="submitter-email">Email</Label>
              <Input
                id="submitter-email"
                width="100%"
                type="email"
                placeholder="Your email address"
                required={isInteractive}
                value={submitterEmail}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setSubmitterEmail(e.target.value)
                }
                disabled={!isInteractive}
              />
            </FormColumn>
          </FormRow>
        )}

        {/* Submit button — shown in fill and preview modes, hidden when parent owns the submit button */}
        {!isReadOnly && !onRequestSubmit && (
          <FormRow>
            <FormColumn sx={{ alignItems: 'flex-end' }}>
              <Button
                variant="primary"
                disabled={!isInteractive}
                onClick={handleValidateAndSubmit}
              >
                Submit
              </Button>
            </FormColumn>
          </FormRow>
        )}
      </Form>
    </Box>
  );
};

export default HazardSurveyResult;
